import React, {useEffect, useState} from "react"
import {Box, Typography, CircularProgress, Chip, Link, Button} from "@mui/material"
import NorthEastIcon from "@mui/icons-material/NorthEast"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {Link as RouterLink} from "react-router-dom"
import {format} from "date-fns"

import {ConversationDetail} from "../Cases/ConversationDetail"

import {api} from "@indebted/api"
import type {ConversationSummary} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {colors} from "@indebted/theme/colors"

function threadSubject(subject: string): string {
	return subject.trim() === "" ? "Conversation" : subject
}

function formatLastActivity(lastActivityAt: string): string {
	return format(new Date(lastActivityAt), "d MMM yyyy, HH:mm")
}

function Conversations({customerID}: {customerID: string}) {
	const {notification} = useNotification()
	const [conversations, setConversations] = useState<ConversationSummary[]>([])
	const [loading, setLoading] = useState(true)
	const [loadFailed, setLoadFailed] = useState(false)
	const [selectedConversationID, setSelectedConversationID] = useState<string | null>(null)
	const [reloadToken, setReloadToken] = useState(0)

	useEffect(() => {
		let active = true
		setLoading(true)
		setLoadFailed(false)
		setSelectedConversationID(null)
		api.customer
			.getCustomerConversations(customerID)
			.then((data) => {
				if (active) setConversations(data)
			})
			.catch(() => {
				if (!active) return
				notification.error("Failed to load conversations")
				setLoadFailed(true)
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
		}
	}, [customerID, notification, reloadToken])

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" p={3}>
				<CircularProgress />
			</Box>
		)
	}

	if (loadFailed) {
		return (
			<Box p={3} display="flex" alignItems="center" gap={2}>
				<Typography color="error">Failed to load conversations</Typography>
				<Button size="small" onClick={() => setReloadToken((token) => token + 1)}>
					Retry
				</Button>
			</Box>
		)
	}

	if (conversations.length === 0) {
		return (
			<Box p={3}>
				<Typography variant="body2" color="textSecondary">
					No conversations
				</Typography>
			</Box>
		)
	}

	return (
		<Box p={2} display="flex" flexDirection="column" gap={1}>
			{conversations.map((conversation) => (
				<ConversationRow
					key={conversation.id}
					customerID={customerID}
					conversation={conversation}
					selected={conversation.id === selectedConversationID}
					onSelect={() =>
						setSelectedConversationID((current) => (current === conversation.id ? null : conversation.id))
					}
				/>
			))}
			{selectedConversationID && (
				<Box sx={{ml: 1, pl: 2, borderLeft: "3px solid", borderLeftColor: "primary.main"}}>
					<ConversationDetail
						key={selectedConversationID}
						customerID={customerID}
						conversationID={selectedConversationID}
					/>
				</Box>
			)}
		</Box>
	)
}

function ConversationRow({
	customerID,
	conversation,
	selected,
	onSelect,
}: {
	customerID: string
	conversation: ConversationSummary
	selected: boolean
	onSelect: () => void
}) {
	return (
		<Box
			display="flex"
			alignItems="center"
			flexWrap="wrap"
			gap={1.5}
			sx={{
				border: "1px solid",
				borderColor: selected ? "primary.main" : colors.gray["200"],
				borderRadius: "4px",
				bgcolor: selected ? "action.selected" : colors.gray["100"],
				p: 1.5,
				"&:hover": {bgcolor: selected ? "action.selected" : "action.hover"},
			}}
		>
			<Box
				component="button"
				type="button"
				onClick={onSelect}
				aria-pressed={selected}
				display="flex"
				alignItems="center"
				gap={1.5}
				flexGrow={1}
				minWidth={0}
				sx={{
					font: "inherit",
					color: "inherit",
					textAlign: "left",
					background: "none",
					border: "none",
					p: 0,
					cursor: "pointer",
				}}
			>
				<ExpandMoreIcon
					fontSize="small"
					sx={{
						flexShrink: 0,
						color: "action.active",
						transition: "transform 0.2s",
						transform: selected ? "rotate(0deg)" : "rotate(-90deg)",
					}}
				/>
				<Box flexGrow={1} minWidth={0}>
					<Typography variant="subtitle2" noWrap>
						{threadSubject(conversation.subject)}
					</Typography>
					<Typography variant="caption" color="textSecondary">
						{formatLastActivity(conversation.lastActivityAt)}
					</Typography>
				</Box>
				<Chip label={conversation.status} size="small" variant="outlined" />
			</Box>
			{conversation.caseIds.map((caseID) => (
				<Link
					key={caseID}
					component={RouterLink}
					to={`/customers/${customerID}/cases?CaseID=${caseID}`}
					variant="caption"
					underline="hover"
					sx={{display: "inline-flex", alignItems: "center", gap: 0.25}}
				>
					Case
					<NorthEastIcon sx={{fontSize: 12}} />
				</Link>
			))}
		</Box>
	)
}

export {Conversations}
